<script setup>
import EmailingAutomationConfigurationForm from '@frontJs/components/forms/projectManagement/modules/EmailingAutomationConfigurationForm.vue';
import EmailingAutomationDeliverabilityStatisticsTable from '@frontJs/components/tables/projectManagement/modules/EmailingAutomationDeliverabilityStatisticsTable.vue';
import EmailingAutomationDetailedStatisticsTable from '@frontJs/components/tables/projectManagement/modules/EmailingAutomationDetailedStatisticsTable.vue';
import EmailingAutomationEmailExclusionTable from '@frontJs/components/tables/projectManagement/modules/EmailingAutomationEmailExclusionTable.vue';
import EmailingAutomationGeneralStatisticsTable from '@frontJs/components/tables/projectManagement/modules/EmailingAutomationGeneralStatisticsTable.vue';
import EmailingAutomationPromoPriceRuleTable from '@frontJs/components/tables/projectManagement/modules/EmailingAutomationPromoPriceRuleTable.vue';
import EmailingAutomationTable from '@frontJs/components/tables/projectManagement/modules/EmailingAutomationTable.vue';
import EmailingAutomationUnsubscriptionTable from '@frontJs/components/tables/projectManagement/modules/EmailingAutomationUnsubscriptionTable.vue';
import EmailingAutomationVariableTable from '@frontJs/components/tables/projectManagement/modules/EmailingAutomationVariableTable.vue';
import TenantReportEmailingAutomationTable from '@frontJs/components/tables/projectManagement/modules/tenantReports/EmailingAutomationTable.vue';
import ProjectManagementLayout from '@frontJs/layouts/ProjectManagementLayout.vue';
import { Head } from '@inertiajs/vue3';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import PageTitle from '@js/components/titles/PageTitle.vue';
import { h, onMounted, ref } from 'vue';

const { tenant, configuration } = defineProps({
    tenant: {
        type: Object,
        required: false,
    },
    configuration: {
        type: Object,
        required: false,
    },
});

const loading = ref(true);

const data = ref({
    isActive: 0,
    isBeingTested: 0,
    all: 0,
});

function updateData() {
    loading.value = true;

    Promise.all([
        axios.post(
            route(
                'front.projectManagement.modules.postEmailingAutomationCount',
            ),
            {
                isActive: true,
            },
        ),
        axios.post(
            route(
                'front.projectManagement.modules.postEmailingAutomationCount',
            ),
            {
                isBeingTested: true,
            },
        ),
        axios.post(
            route(
                'front.projectManagement.modules.postEmailingAutomationCount',
            ),
        ),
    ])
        .then(([isActiveData, isBeingTestedData, allData]) => {
            data.value.isActive = isActiveData.data.count;
            data.value.isBeingTested = isBeingTestedData.data.count;
            data.value.all = allData.data.count;
        })
        .catch((error) => {
            console.error(error);
        })
        .finally(() => {
            loading.value = false;
        });
}

onMounted(() => {
    updateData();
});

defineOptions({ layout: ProjectManagementLayout });
</script>

<template>
    <Head>
        <title>
            Emailings automatisés - Modules - Gestion de projet - Datakepler
        </title>
    </Head>

    <PageTitle :tenant-name="tenant ? tenant.name : undefined">
        Gestion de projet - Modules - Emailings automatisés
    </PageTitle>

    <TabLayout
        v-if="tenant && configuration"
        :tabs="{
            automations: {
                title: 'Automatisations',
                content: h(EmailingAutomationTable, { tenant }),
            },
            statistics: {
                title: 'Statistiques',
                items: {
                    general: {
                        title: 'Général',
                        content: h(EmailingAutomationGeneralStatisticsTable, {
                            tenant,
                        }),
                    },
                    detailedStatistics: {
                        title: 'Détaillées',
                        content: h(EmailingAutomationDetailedStatisticsTable, {
                            tenant,
                        }),
                    },
                    deliverability: {
                        title: 'Délivrabilité',
                        content: h(
                            EmailingAutomationDeliverabilityStatisticsTable,
                            {
                                tenant,
                            },
                        ),
                    },
                },
            },
            variables: {
                title: 'Variables',
                content: h(EmailingAutomationVariableTable, { tenant }),
            },
            promoCodes: {
                title: 'Codes promo',
                content: h(EmailingAutomationPromoPriceRuleTable, { tenant }),
            },
            blacklist: {
                title: 'Liste noire',
                content: h(EmailingAutomationEmailExclusionTable, { tenant }),
            },
            unsubscriptions: {
                title: 'Désabonnements',
                content: h(EmailingAutomationUnsubscriptionTable, { tenant }),
            },
            configuration: {
                title: 'Configuration',
                content: h(EmailingAutomationConfigurationForm, {
                    configuration,
                    tenant,
                }),
            },
        }"
    />

    <TabLayout
        v-else
        :tabs="{
            actives: {
                title: 'Actifs',
                total: data.isActive,
                content: h(TenantReportEmailingAutomationTable, {
                    isActive: true,
                }),
            },
            tests: {
                title: 'Tests',
                total: data.isBeingTested,
                content: h(TenantReportEmailingAutomationTable, {
                    isBeingTested: true,
                }),
            },
            all: {
                title: 'Tous',
                total: data.all,
                content: h(TenantReportEmailingAutomationTable),
            },
        }"
        :loading="loading"
    />
</template>
